<template>
  <div class="uploader-container">
    <section class="top-section flex items-center justify-between">
      <h3 class="m-0 font-normal text-2xl">Bulk Create Zoom Classes</h3>
      <div class="flex justify-end items-center">
        <button
          v-if="errors.length > 0"
          class="text-red mr-4 font-normal hover:bg-transparent hover:text-red  my-0"
          @click="showErrors"
        >
          ⚠ Failed to schedule few classes, click here see
        </button>
        <AddClassButton
          class="font-normal mr-4"
          style="line-height:1;"
          @click.native="addNewClass"
        />
        <CSVFileUploadButton
          class="upload-btn shadow-md"
          @change="handleFileInput"
        />
        <button
          class="schedule-btn ml-4 shadow-md"
          :disabled="classes.length === 0 || !isDataValid || savingClass"
          @click="handleSchedule"
        >
          <span v-if="savingClass"
            >Scheduling Classes... ({{ savedClassesCount }}/{{
              classes.length
            }})</span
          >
          <span v-else
            >Schedule Classes
            {{ classes.length > 0 ? `(${classes.length})` : "" }}</span
          >
        </button>
      </div>
    </section>
    <div
      v-if="classes.length === 0"
      class="text-center text-lg  quick-action-section bg-grey-lightest rounded-sm shadow"
      @click="handleClick"
      @drop.prevent.stop="handleDrop"
      @dragover.prevent="handleDragover"
      @dragleave="handleDragleave"
    >
      <div v-if="droppedFile && droppedFile.name" class="fadeIn">
        <p class="text-green">{{ droppedFile.name }}</p>
        <p class="text-sm mt-4">Please wait...</p>
      </div>
      <div v-else>
        <div v-if="dragEnabled" class="fadeIn">
          <p class="text-lg text-grey-darker">
            Drop here to parse file
          </p>
        </div>
        <div v-else class="fadeIn">
          <i class="material-icons file_upload text-4xl text-grey-darker">
            file_upload
          </i>
          <p class="text-lg text-grey-darker">
            Drag and drop a CSV file or click to upload
          </p>
          <p class="text-sm text-grey-dark mt-4">
            <button
              class="bg-transparent font-normal hover:bg-transparent focus:bg-transparent text-blue hover:text-blue focus:text-blue rounded-sm p-0"
              @click.prevent.stop="downloadSampleCSVFile"
            >
              Click here
            </button>
            to download a sample CSV file
          </p>
        </div>
        <CSVFileUploadButton
          ref="uploadButton"
          style="left: -9999px;"
          class="absolute"
          @change="handleFileInput"
        />
      </div>
    </div>
    <section v-else class="main-section mt-4 edyst-form-group">
      <div>
        <div
          v-for="(item, idx) in classes"
          :key="`class-item-${idx}`"
          class="class-item fadeIn relative"
          :style="!isClassDataValid(item) ? 'border:1px solid red;' : ''"
        >
          <span class="count-item">{{ idx + 1 }}</span>
          <button
            class="delete-btn text-xs p-1 hover:bg-grey-light focus:bg-grey-light"
            @click="deleteItem({ item, idx })"
          >
            ❌
          </button>
          <div class="flex items-center justify-between">
            <div class="input-item">
              <label :for="`host email-${idx}`">Host Email</label>
              <input
                :id="`host email-${idx}`"
                v-model="item.host_email"
                type="email"
                name="host email"
                placeholder="Enter host email"
              />
            </div>
            <div class="input-item">
              <label for="start time">Start Time</label>
              <EdystDatePicker
                v-model="item.start_date"
                :selected="item.start_date"
                placeholderText="Select start date"
                class="date-picker"
              />
            </div>
            <div class="input-item">
              <label for="end time">End Time</label>
              <EdystDatePicker
                v-model="item.end_date"
                :selected="item.end_date"
                placeholderText="Select end date"
                class="date-picker"
              />
            </div>
            <div class="input-item w-full flex flex-col">
              <label :for="`class name-${idx}`">Class Name</label>
              <input
                :id="`class name-${idx}`"
                v-model="item.event_name"
                type="text"
                placeholder="Enter class name"
                class="w-full class-name"
              />
            </div>
          </div>
          <div class="w-full mt-3">
            <label
              >Register Cohort ({{
                `${usersCountOfRegisteredCohorts(
                  item.registered_cohorts_list
                )} Students`
              }})
            </label>
            <VueSelect
              v-model="item.registered_cohorts_list"
              placeholder="Select a Cohort"
              :closeOnSelect="false"
              :clearSearchOnSelect="false"
              :options="cohortList"
              :reduce="cohort => cohort.name"
              :multiple="true"
              label="name"
            />
          </div>
        </div>
      </div>
    </section>
    <section class="bottom-section">
      <AddClassButton @click.native="addNewClass" />
    </section>
    <VModal ref="errors_modal" name="errors_modal" width="600" height="50%">
      <div class="errors-modal leading-normal p-8">
        <div v-for="(err, idx) in errors" :key="err + idx" class="mb-4">
          <div class="text-lg">{{ idx + 1 }}) {{ err.item.event_name }}:</div>
          <div class="ml-4 text-red-light">⚠ {{ err.message }}</div>
        </div>
      </div>
    </VModal>
  </div>
</template>

<script>
import VueSelect from "vue-select"
import Papa from "papaparse"
import { isDate, differenceInSeconds } from "date-fns"
import EdystDatePicker from "@/components/ReactDatePicker/DatePicker"
import {
  getCohorts,
  bulkCreateEvent,
  bulkCreateEventStatus,
} from "@/apis/events/"
import CSVFileUploadButton from "@/components/Event/CSVFileUploadButton"
import AddClassButton from "@/components/Event/AddNewClass"

export default {
  components: {
    EdystDatePicker,
    VueSelect,
    CSVFileUploadButton,
    AddClassButton,
  },
  data() {
    return {
      dragEnabled: false,
      droppedFile: {},
      classes: [],
      cohortList: [],
      hasWrongDate: false,
      currentClassIndex: 0,
      savedClassesCount: 0,
      savingClass: false,
      errors: [],
    }
  },
  computed: {
    isDataValid() {
      return this.classes.every(item => this.isClassDataValid(item))
    },
    getOtherRequiredKeys() {
      return {
        description: "",
        duration_secs: 0,
        event_key_id: "",
        event_key_type: "liveclass",
        liveclass_provider: "zoom",
        max_registrations: 0,
        payment_type: "paid",
        registered_users_list: [],
        registration_deadline: 0,
        meeting_details: { settings: {} },
      }
    },
  },
  created() {
    getCohorts()
      .then(cohort_res => {
        this.cohortList = cohort_res.data.items.map(item => ({
          ...item,
          name: `${item.name} (${item.user_count || 0})`,
        }))
      })
      .catch(err => {
        console.error(err)
        this.$noty.error("Failed to fetch cohort list")
      })
  },
  methods: {
    handleDragleave() {
      this.dragEnabled = false
    },
    handleDragover() {
      this.dragEnabled = true
    },
    handleClick() {
      this.$refs.uploadButton.click()
    },
    handleDrop(event) {
      this.classes = []
      const { files } = event.dataTransfer
      event.preventDefault()
      this.droppedFile = files[0]
      setTimeout(() => {
        this.parseCSVFile(this.droppedFile)
      }, 500)
    },
    showErrors() {
      this.$modal.show("errors_modal")
    },
    isClassDataValid(item) {
      if (
        !item.event_name ||
        !item.host_email ||
        !item.start_date ||
        this.isInvalidDate(item.start_date) ||
        !item.end_date ||
        this.isInvalidDate(item.end_date)
      ) {
        return false
      }
      return true
    },
    async deleteItem(data) {
      this.classes.splice(data.idx, 1)
    },
    downloadSampleCSVFile() {
      const data = [
        {
          host_email: "team@edyst.com",
          start_date: "2021/05/25 17:30",
          end_date: "2021/05/25 19:30",
          event_name: "Intro to JS",
        },
      ]
      const csv = Papa.unparse(data)
      const downloadLink = document.createElement("a")
      downloadLink.download = "Sample-CSV-liveclass-bulk-upload-edyst.csv"
      downloadLink.href = window.URL.createObjectURL(
        new Blob([csv], { type: "text/csv" })
      )
      downloadLink.click()
    },
    handleFileInput(event) {
      this.classes = []
      const file = event.target.files[0]
      this.droppedFile = file
      setTimeout(() => {
        this.parseCSVFile(file)
        // reset the file picker value
        event.target.value = ""
      }, 500)
    },
    parseCSVFile(file) {
      Papa.parse(file, {
        header: true,
        complete: results => {
          if (!results.data || results.data.length === 0) {
            this.$noty.error("Looks like CSV file doesn't have any data")
            return
          }
          this.prepareCSVData(results.data)
          // show any errors we have
          if (results.errors.length > 0) {
            let rows = results.errors.map(e => e.row)
            rows = rows.join(", ")
            setTimeout(() => {
              this.$noty.error(
                `Failed to parse these rows: ${rows}. It is possible that few cells or values are missing. Please ignore if those are empty rows`
              )
            }, 1000)
          }
        },
        erorr: err => {
          console.error(err)
          this.$noty.error(
            "Failed to parse the file. Make sure that you're uploading correct csv file."
          )
        },
      })
      this.droppedFile = {}
      this.dragEnabled = false
    },
    prepareCSVData(parsedData) {
      // loop over every item and prepare the object
      const classList = parsedData.map(item => ({
        ...item,
        start_date: this.getValidDate({
          date: item.start_date,
          event_name: item.event_name,
        }),
        end_date: this.getValidDate({
          date: item.end_date,
          event_name: item.event_name,
        }),
        registered_cohorts_list: [],
      }))

      this.classes = classList
      if (this.hasWrongDate) {
        setTimeout(() => {
          this.showError(
            "Date formats are incorrect for one or more classes. Please select the dates for empty date fields"
          )
        }, 1800)
        this.hasWrongDate = false
      }
    },
    showError(msg) {
      this.$noty.warning(msg, {
        timeout: 8000,
      })
    },
    getValidDate(item) {
      let date = new Date(item.date)
      // verify that it's valid date
      if (this.isInvalidDate(date)) {
        this.hasWrongDate = true
        date = null
      }
      return date
    },
    isInvalidDate(date) {
      return !isDate(date) || Number.isNaN(date.getTime())
    },
    addNewClass() {
      this.classes.push({
        event_name: "",
        start_date: null,
        end_date: null,
        registered_cohorts_list: [],
        host_email: "",
      })
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    scrollToBottom() {
      window.scrollTo(0, document.body.scrollHeight)
    },
    usersCountOfRegisteredCohorts(cohort) {
      if (cohort) {
        return cohort.reduce(
          (currentCount, currentCohort) =>
            currentCount + currentCohort.user_count,
          0
        )
      }
      return 0
    },
    handleSchedule() {
      this.errors = []
      this.createLiveClasses()
    },

    fetchUploadStatus() {
      bulkCreateEventStatus(this.upload_task_uuid)
        .then(res => {
          this.savedClassesCount = 0
          // if in queued or inprogress state then wait for sometime and call again
          if (
            res.data.status === "queued" ||
            res.data.status === "inprogress"
          ) {
            setTimeout(this.fetchUploadStatus, 3000)
            if (res.data.status === "inprogress") {
              this.savedClassesCount = res.data.saved_classes_count
            }
          }
          // if in success state, show swal
          else if (res.data.status === "completed") {
            this.savingClass = false
            this.errors = []

            for (let i = 0; i < res.data.errors.length; i++) {
              const err = res.data.errors[i]
              const errorObj = {
                message: err.message,
                item: this.classes[err.idx],
                itemIndex: err.idx,
              }
              this.errors.push(errorObj)
            }

            const msg =
              res.data.errors.length > 0
                ? "Failed to schedule few classes, please check the errors and try again"
                : "Successfully scheduled all classes"
            this.$swal({
              icon: res.data.errors.length > 0 ? "warning" : "success",
              text: msg,
              buttons: {
                cancel: false,
                confirm: {
                  text: "Close",
                  value: true,
                },
              },
            })
            this.keepFailedClasses()
          }
        })
        .catch(err => {
          clearInterval(this.intervalId)
          console.error(err)
        })
    },

    createLiveClasses() {
      this.savingClass = true

      // create the payload
      const payload = []
      for (let i = 0; i < this.classes.length; i++) {
        const event_data = {
          ...this.getOtherRequiredKeys,
          ...this.classes[i],
        }
        event_data.duration_secs = differenceInSeconds(
          event_data.end_date,
          event_data.start_date
        )

        event_data.max_registrations = this.usersCountOfRegisteredCohorts(
          event_data.registered_cohorts_list
        )
        event_data.registered_cohorts_list = event_data.registered_cohorts_list.map(
          item => item.id
        )
        payload.push(event_data)
      }

      bulkCreateEvent(payload)
        .then(res => {
          this.upload_task_uuid = res.data.uuid
          if (res.data.status === "queued") {
            this.$noty.success("Background job added")
          }
          this.fetchUploadStatus()
        })
        .catch(err => {
          console.error(err)
        })
    },
    keepFailedClasses() {
      const indexes = this.errors.map(item => item.itemIndex)
      const failedClasses = []
      this.classes.forEach((item, index) => {
        if (indexes.includes(index)) {
          failedClasses.push(item)
        }
      })
      this.classes = failedClasses
    },
  },
}
</script>

<style lang="postcss" scoped>
.main-section {
  @apply rounded-lg p-3 mb-4;
  margin-bottom: 5rem;
  background: #e1e0e0;

  font-size: 0.9rem;
}

.class-item label {
  @apply mb-1 inline-block;
  color: #5f5f5f;
  font-size: 0.9rem;

  font-weight: bold;
}

.class-item {
  @apply p-3 bg-white w-full mb-3 rounded shadow;
}

.class-item .count-item {
  @apply font-bold absolute;
  top: 0.5rem;
  left: -2rem;

  width: 1.5rem;
  height: 1.5rem;
  color: #6b6b6b;
}

.class-item:last-child {
  @apply mb-0;
}

.class-item .input-item {
  min-width: 13rem;
}

.bottom-section {
  @apply text-right;

  position: fixed;
  bottom: 0;
  width: 85%;
  margin: auto;
  padding: 1rem;
  background: #fff;
}

.quick-action-section {
  display: flex;
  flex-direction: column;
  width: 35rem;
  height: 25rem;
  margin: 5rem auto;
  padding: 2rem;
  justify-content: center;
  align-items: center;
  border: 3px dashed #e4e4e4;
}

.delete-btn {
  @apply absolute rounded-sm;
  top: 0.3rem;

  right: 0.3rem;
}

.uploader-container .add-new-btn,
.uploader-container .upload-btn,
.uploader-container .schedule-btn {
  @apply px-4 py-2 rounded-sm text-white;

  background: #6259c3;
}

.uploader-container .schedule-btn {
  @apply bg-green;
}

.uploader-container button:disabled {
  @apply bg-grey;
}
</style>

<style>
/* global styles for date pickers and form group override */
.uploader-container .main-section.edyst-form-group input {
  margin-top: 0;
  color: #5f5f5f;
}

.main-section.edyst-form-group input:not(.class-name) {
  width: 90%;
}

.uploader-container .v-select .selected-tag {
  color: #5f5f5f;
}
</style>
